import React, { useEffect, useState, Fragment } from 'react';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { withStyles } from '@material-ui/core/styles';
import Tabs from '@material-ui/core/Tabs';

import {
    Text,
    PatientSessionCard,
    NoInformation,
    Progress,
    Header,
    PatientDetailTab,
    ProviderInfoCard,
    SessionSummaryCard,
    NoteFromProviderCard,
    NoteCard,
    ContainedButton,
    SelectedActivityCard,
    MindgymAppCard,
    StyledCard,
} from '../../components';

import { patientPageWidth } from '../../constant';

import { GET_ENDED_SESSIONS, GET_ENDED_SESSION, UPDATE_MY_NOTE } from '../../services/patient';

import { mindgymReport } from '../../services';

import { processMindgymReport } from '../../utils/mindgym';

import { alertError, alertSuccess } from '../../redux/stateActions';

const Summary = ({ history }) => {
    const theme = useTheme();
    const mdUp = useMediaQuery(theme.breakpoints.up('sm'));
    const dispatch = useDispatch();

    const { id } = useParams();

    const user = useSelector((state) => state.user);

    const [selectedTab, setSelectedTab] = useState(0);
    const [date, setDate] = useState(null);
    const [selectedSession, setSelectedSession] = useState({});

    const tabsChangeHandler = (event, newValue) => {
        setSelectedTab(newValue);
    };

    useEffect(() => {
        setDate(dayjs().format('YYYY-MM-DD'));
    }, []);

    /*--------------- Get 1 Session Summary ---------------*/

    const {
        data: getSessionSummaryData,
        error: getSessionSummaryError,
        loading: getSessionSummaryLoading,
        refetch: getSessionSummaryRefetch,
    } = useQuery(GET_ENDED_SESSION, {
        variables: {
            id: id,
            patientId: user.patient.id,
        },
        skip: _.isEmpty(user) || !id,
    });

    useEffect(() => {
        if (getSessionSummaryError) {
            dispatch(alertError(`Cannot get Session Summary`));
        }
        if (getSessionSummaryData) {
            setSelectedSession(getSessionSummaryData.session);
            setNote(getSessionSummaryData.session.patient_summaries[0].myNote);
            handleGetMindGymReport(
                getSessionSummaryData.session?.meet?.staff?.id,
                getSessionSummaryData.session.id
            );
        }
    }, [getSessionSummaryError, getSessionSummaryData]);

    /*--------------- Get All Session Summary ---------------*/
    const [oneOnOneSessions, setOneOnOneSessions] = useState([]);
    const [groupSessions, setGroupSessions] = useState([]);

    const {
        data: getEndedSessionsData,
        error: getEndedSessionsError,
        loading: getEndedSessionsLoading,
    } = useQuery(GET_ENDED_SESSIONS, {
        variables: {
            id: user.patient.id,
        },
        skip: _.isEmpty(user) || !date || id,
    });

    useEffect(() => {
        if (getEndedSessionsError) {
            dispatch(alertError(`Cannot get Session Summary`));
        }
        if (getEndedSessionsData) {
            const newOneOnOneSessions = getEndedSessionsData.sessions.filter(
                (session) => session?.meet?.type === 'one'
            );
            setOneOnOneSessions(newOneOnOneSessions);

            const newGroupSessions = getEndedSessionsData.sessions.filter(
                (session) => session?.meet?.type === 'group'
            );
            setGroupSessions(newGroupSessions);
        }
    }, [getEndedSessionsError, getEndedSessionsData]);

    const onSummaryDetail = (session) => {
        history.push(`/summary/${session.id}`);
    };

    const handleGoBack = () => {
        history.goBack();
        setSelectedSession({});
        setNote('');
        setMindgymActivity([]);
    };
    const handleSearch = () => {
        console.log('handleSearch');
    };

    /*--------------- Update My note ---------------*/
    const [note, setNote] = useState('');

    const [updateMyNote, { data: updateMyNoteData, error: updateMyNoteError }] =
        useMutation(UPDATE_MY_NOTE);

    useEffect(() => {
        if (updateMyNoteError) {
            dispatch(alertError(`Cannot update My Note: ${updateMyNoteError.message}`));
        }

        if (updateMyNoteData) {
            dispatch(alertSuccess(`My Noted Updated`));

            getSessionSummaryRefetch();
        }
    }, [updateMyNoteError, updateMyNoteData]);

    const handleNote = (value) => {
        setNote(value);
    };

    const handleSubmit = () => {
        updateMyNote({
            variables: {
                id: selectedSession.patient_summaries[0].id,
                myNote: note,
            },
        });
    };

    const meetType = (tab) => {
        switch (tab) {
            case 0:
                return oneOnOneSessions;
            case 1:
                return groupSessions;
        }
    };

    /*--------------- Mindgym Activity ---------------*/
    const [mindgymActivity, setMindgymActivity] = useState([]);

    const handleGetMindGymReport = async (staffId, sessionId) => {
        try {
            if (!staffId || !sessionId) return;

            const res = await mindgymReport(staffId, sessionId);

            if (res?.data?.statusCode === 200) {
                setMindgymActivity(processMindgymReport(res?.data?.data));
            }
        } catch (error) {
            console.log('error', error);
            dispatch(alertError(`Cannot get Friday Activity: ${error.response?.data?.message}`));
        }
    };

    return (
        <div
            style={{
                margin: '0 15px 20px',
                width: '100%',
                maxWidth: patientPageWidth,
                display: 'flex',
                flexDirection: 'column',
                padding: '0 15px 100px',
            }}
        >
            <Header
                showGoBack1
                onClickGoBack={handleGoBack}
                onSearch={_.isEmpty(selectedSession) && handleSearch}
                childrenContainerStyle={{ textAlign: 'center' }}
            >
                <Text $bold>Session Summary</Text>
            </Header>
            {(getSessionSummaryLoading || getEndedSessionsLoading) && <Progress />}

            {_.isEmpty(selectedSession) ? (
                <Fragment>
                    <StyledTabs
                        value={selectedTab}
                        onChange={tabsChangeHandler}
                        TabIndicatorProps={{ style: { height: '4px', backgroundColor: 'black' } }}
                    >
                        <PatientDetailTab
                            label={<span style={labelStyle}>1 on 1</span>}
                            $active={selectedTab === 0}
                        />
                        <PatientDetailTab
                            style={{ padding: '0 15px' }}
                            label={<span style={labelStyle}>Group</span>}
                            $active={selectedTab === 1}
                        />
                    </StyledTabs>

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            rowGap: '15px',
                            position: 'relative',
                        }}
                    >
                        {!_.isEmpty(meetType(selectedTab)) ? (
                            meetType(selectedTab).map((session) => (
                                <PatientSessionCard
                                    data={session}
                                    key={session.id}
                                    onClick={() => onSummaryDetail(session)}
                                />
                            ))
                        ) : (
                            <NoInformation />
                        )}
                    </div>
                </Fragment>
            ) : (
                <Fragment>
                    <ProviderInfoCard data={selectedSession?.meet?.staff} />
                    <SessionSummaryCard
                        style={{ margin: mdUp ? '20px 0 0 0' : '10px 0 0 0', padding: '20px 15px' }}
                        title="Session Summary"
                        data={selectedSession}
                    />
                    {selectedSession.meet.type === 'one' && (
                        <NoteFromProviderCard
                            style={{
                                margin: mdUp ? '20px 0 0 0' : '10px 0 0 0',
                                padding: '20px 15px',
                            }}
                            data={selectedSession}
                        />
                    )}
                    <NoteCard
                        style={{ margin: mdUp ? '20px 0 0 0' : '10px 0 0 0', padding: '20px 15px' }}
                        value={note}
                        onChange={
                            !selectedSession.patient_summaries[0].myNote
                                ? (event) => handleNote(event.target.value)
                                : false
                        }
                    />
                    {!_.isEmpty(mindgymActivity) && (
                        <StyledCard style={{ marginTop: '20px' }}>
                            <Text $color="pink" style={{ fontSize: '14px' }}>
                                Friday Homework
                            </Text>
                            <div
                                style={{
                                    marginTop: '10px',
                                    display: 'grid',
                                    gridTemplateColumns: mdUp ? 'auto auto' : 'auto',
                                    columnGap: '24px',
                                    rowGap: mdUp ? '16px' : '10px',
                                    overflow: 'hidden',
                                }}
                            >
                                {mindgymActivity.map((item) => (
                                    <SelectedActivityCard
                                        key={`selected-activity-${item.type}`}
                                        data={item}
                                        padding={mdUp ? '15px 19px 19px 19px' : '10px'}
                                    />
                                ))}
                            </div>
                            <MindgymAppCard mdUp={mdUp} />
                        </StyledCard>
                    )}
                    {!selectedSession.patient_summaries[0].myNote && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <ContainedButton
                                style={{ width: mdUp ? '240px' : '100%', marginTop: '20px' }}
                                disabled={Boolean(selectedSession.patient_summaries[0].myNote)}
                                onClick={handleSubmit}
                            >
                                Save
                            </ContainedButton>
                        </div>
                    )}
                </Fragment>
            )}
        </div>
    );
};

Summary.propTypes = {
    history: PropTypes.object,
};

export default Summary;

const StyledTabs = withStyles({
    root: {
        marginBottom: '24px',
    },
    flexContainer: {
        justifyContent: 'center',
    },
})(Tabs);

const labelStyle = {
    fontWeight: 'bold',
    margin: '0 15px',
};
